import { DateTime } from "luxon";
import { 
  getSolarTerm 
} from "./solarTermsData.js";

export function getGanZhiWithTranslation(index, language = "en") {
  const chineseStems = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];
  const chineseBranches = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"];
  
  const englishStems = ["Jia", "Yi", "Bing", "Ding", "Wù", "Ji", "Geng", "Xin", "Ren", "Gui"];
  const englishBranches = ["Zi", "Chou", "Yin", "Mao", "Chen", "Si", "Wǔ", "Wei", "Shen", "You", "Xu", "Hai"];
  
  const chineseStem = chineseStems[index % 10];
  const chineseBranch = chineseBranches[index % 12];
  const englishStem = englishStems[index % 10];
  const englishBranch = englishBranches[index % 12];
  
  if (language === 'zh') {
    return chineseStem + chineseBranch;
  } else {
    return `${englishStem} ${englishBranch}`;
  }
}

// Calculate year pillar based on Start of Spring (立春)
function getYearPillar(dt, language = "en") {
  const year = dt.year;
  const startOfSpringISO = getSolarTerm(year, "Start of Spring");
  const startOfSpringDt = DateTime.fromISO(startOfSpringISO);
  console.log("Start of Spring for year", year, "is at", startOfSpringISO);
  const adjustedYear = dt < startOfSpringDt ? year - 1 : year;
  const index = (adjustedYear - 1864) % 60;
  const pillar = getGanZhiWithTranslation(index, language);
  return { pillar, index };
}

function getMonthPillar(dt, yearPillarIndex, language = "en") {
  const termNames = [
    "Start of Spring", "Awakening of Insects", "Pure Brightness", "Start of Summer",
    "Grain in Ear", "Minor Heat", "Start of Autumn", "White Dew",
    "Cold Dew", "Start of Winter", "Major Snow", "Minor Cold"
  ];
  
  const startOfSpring = DateTime.fromISO(getSolarTerm(dt.year, "Start of Spring"));
  let monthIndex = 0;

  if (dt < startOfSpring) {
    // Before Start of Spring: belongs to previous solar year
    // Check if it's Chou month (11) or Zi month (10)
    // Minor Cold for the current solar year is in the previous calendar year's data structure
    // e.g. Jan 2025 is in 2024's data structure under "Minor Cold"
    let minorCold;
    try {
      minorCold = DateTime.fromISO(getSolarTerm(dt.year - 1, "Minor Cold"));
    } catch (e) {
      console.warn("Could not find Minor Cold for previous year, defaulting to Zi month logic based on date");
      // Approximate Minor Cold is Jan 5/6
      minorCold = DateTime.fromObject({ year: dt.year, month: 1, day: 6 });
    }
    monthIndex = dt >= minorCold ? 11 : 10;
  } else {
    // After Start of Spring: belongs to current solar year
    for (let i = 1; i < 12; i++) {
      const termName = termNames[i];
      // Minor Cold (Index 11) is in the next calendar year
      const year = i === 11 ? dt.year + 1 : dt.year;
      const termDate = DateTime.fromISO(getSolarTerm(year, termName));
      
      if (dt >= termDate) {
        monthIndex = i;
      } else {
        break;
      }
    }
  }

  // 五虎遁口诀：甲己丙作首，乙庚戊为头，丙辛寻庚起，丁壬顺行流，戊癸甲为首
  // Calculate Month Stem based on Year Stem
  const yearStemIndex = yearPillarIndex % 10;
  
  // Formula: (2 + (yearStemIndex % 5) * 2) % 10
  const startMonthStemIndex = (2 + (yearStemIndex % 5) * 2) % 10;
  
  // Current Month Stem
  const monthStemIndex = (startMonthStemIndex + monthIndex) % 10;
  
  // Current Month Branch (0 is Yin month, which is index 2 in branches)
  const monthBranchIndex = (2 + monthIndex) % 12;
  
  // Calculate 60 JiaZi index
  const index = ((6 * monthStemIndex - 5 * monthBranchIndex) % 60 + 60) % 60;
  
  return getGanZhiWithTranslation(index, language);
}

function getDayPillar(dt, language = "en") {
  const baseDateInTargetZone = DateTime.fromObject(
    { year: 1899, month: 12, day: 21, hour: 23, minute: 0, second: 0, millisecond: 0 },
    { zone: dt.zone }
  );
  const diff = dt.diff(baseDateInTargetZone, 'days').days;
  const index = (Math.floor(diff) % 60 + 60) % 60;
  const pillar = getGanZhiWithTranslation(index, language);
  return { pillar, index };
}

function getHourPillar(dayStemIndex, dt, language = "en") {
  let hourIndex;
  const hour = dt.hour;
  if (hour === 23 || hour === 0) {
    hourIndex = 0;
  } else {
    hourIndex = Math.floor((hour + 1) / 2);
  }
  // 五鼠遁口诀: 甲己还加甲，乙庚丙作初，丙辛从戊起，丁壬庚子居，戊癸何方发，壬子是真途
  const dayStem = dayStemIndex % 10;
  let startStemIndex;
  switch (dayStem) {
    case 0: // 甲
    case 5: // 己
      startStemIndex = 0; // 甲
      break;
    case 1: // 乙
    case 6: // 庚
      startStemIndex = 2; // 丙
      break;
    case 2: // 丙
    case 7: // 辛
      startStemIndex = 4; // 戊
      break;
    case 3: // 丁
    case 8: // 壬
      startStemIndex = 6; // 庚
      break;
    case 4: // 戊
    case 9: // 癸
      startStemIndex = 8; // 壬
      break;
    default:
      startStemIndex = 0;
      break;
  }
  const hourStemIndex = (startStemIndex + hourIndex) % 10;
  const hourBranchIndex = hourIndex;
  const index = ((6 * hourStemIndex - 5 * hourBranchIndex) % 60 + 60) % 60;
  return getGanZhiWithTranslation(index, language);
}

export function computeBaziPillars(dt, language = "en") {
  console.log("Computing BaZi pillars for:", dt.toISO(), "Language:", language);
  
  const {pillar: yearPillar, index: yearIndex} = getYearPillar(dt, language);
  const monthPillar = getMonthPillar(dt, yearIndex, language);
  const {pillar: dayPillar, index: dayIndex} = getDayPillar(dt, language);
  const hourPillar = getHourPillar(dayIndex, dt, language);

  return {
    yearPillar,
    monthPillar,
    dayPillar,
    hourPillar
  };
}

// Helper to get stem/branch from string like "JiaZi (甲子)" or "甲子" or "Jia Zi"
function parsePillarString(pillarStr) {
  if (!pillarStr) return "";
  // Extract Chinese characters
  const match = pillarStr.match(/[\u4e00-\u9fa5]+/);
  if (match) {
    return match[0]; // "甲子"
  }
  return pillarStr; // Return as is (English)
}

// Helper to get index from GanZhi string
function getGanZhiIndex(ganZhi) {
  const chineseStems = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];
  const chineseBranches = ["子", "丑", "寅", "卯", "辰", "巳", "午", "未", "申", "酉", "戌", "亥"];
  
  const englishStems = ["Jia", "Yi", "Bing", "Ding", "Wù", "Ji", "Geng", "Xin", "Ren", "Gui"];
  const englishBranches = ["Zi", "Chou", "Yin", "Mao", "Chen", "Si", "Wǔ", "Wei", "Shen", "You", "Xu", "Hai"];

  let stemIndex = -1;
  let branchIndex = -1;

  if (/[\u4e00-\u9fa5]/.test(ganZhi)) {
    if (ganZhi.length < 2) return -1;
    const stem = ganZhi[0];
    const branch = ganZhi[1];
    stemIndex = chineseStems.indexOf(stem);
    branchIndex = chineseBranches.indexOf(branch);
  } else {
    const parts = ganZhi.split(' ');
    if (parts.length === 2) {
      stemIndex = englishStems.indexOf(parts[0]);
      branchIndex = englishBranches.indexOf(parts[1]);
    }
  }
  
  if (stemIndex === -1 || branchIndex === -1) return -1;
  
  return ((6 * stemIndex - 5 * branchIndex) % 60 + 60) % 60;
}

export function computeDayun(baziPillars, gender, birthDateTime, language = "en") {
  const emptyResult = { list: [], startInfo: null };
  if (!baziPillars || !birthDateTime) return emptyResult;

  // 1. Parse Year Pillar to get Year Stem
  const yearPillarStr = baziPillars.yearPillar;
  const yearGanZhi = parsePillarString(yearPillarStr);
  if (!yearGanZhi) return emptyResult;
  
  const chineseStems = ["甲", "乙", "丙", "丁", "戊", "己", "庚", "辛", "壬", "癸"];
  const englishStems = ["Jia", "Yi", "Bing", "Ding", "Wù", "Ji", "Geng", "Xin", "Ren", "Gui"];
  
  let yearStemIndex = -1;
  if (/[\u4e00-\u9fa5]/.test(yearGanZhi)) {
      yearStemIndex = chineseStems.indexOf(yearGanZhi[0]);
  } else {
      const parts = yearGanZhi.split(' ');
      if (parts.length > 0) {
          yearStemIndex = englishStems.indexOf(parts[0]);
      }
  }
  
  if (yearStemIndex === -1) return emptyResult;

  const isYearYang = yearStemIndex % 2 === 0; // 0, 2, 4... are Yang
  
  // 2. Determine Direction
  const isMale = gender === "male";
  // Yang Male (Male + Yang Year) -> Forward
  // Yin Female (Female + Yin Year) -> Forward
  // Yin Male (Male + Yin Year) -> Backward
  // Yang Female (Female + Yang Year) -> Backward
  const isForward = (isMale && isYearYang) || (!isMale && !isYearYang);
  
  // 3. Find Nearest Major Solar Term (Jie)
  let birthDt;
  if (typeof birthDateTime === 'string') {
      birthDt = DateTime.fromISO(birthDateTime, { setZone: true });
      if (!birthDt.isValid) {
          // Try SQL format if ISO fails
          birthDt = DateTime.fromSQL(birthDateTime, { setZone: true });
      }
  } else {
      birthDt = birthDateTime;
  }
  
  if (!birthDt || !birthDt.isValid) return emptyResult;

  const year = birthDt.year;
  
  // Get all Jie terms for current year and adjacent years
  const getJieTerms = (y) => {
    const terms = [
      "Start of Spring", "Awakening of Insects", "Pure Brightness", "Start of Summer",
      "Grain in Ear", "Minor Heat", "Start of Autumn", "White Dew",
      "Cold Dew", "Start of Winter", "Major Snow", "Minor Cold"
    ];
    return terms.map(term => {
        try {
            return {
                name: term,
                date: DateTime.fromISO(getSolarTerm(y, term))
            };
        } catch (e) {
            return null;
        }
    }).filter(t => t !== null);
  };
  
  let terms = [
    ...getJieTerms(year - 1),
    ...getJieTerms(year),
    ...getJieTerms(year + 1)
  ];
  
  // Sort by date
  terms.sort((a, b) => a.date - b.date);
  
  let targetTerm;
  if (isForward) {
    // Find first term > birthDt
    targetTerm = terms.find(t => t.date > birthDt);
  } else {
    // Find last term < birthDt
    // Reverse iterate
    for (let i = terms.length - 1; i >= 0; i--) {
      if (terms[i].date < birthDt) {
        targetTerm = terms[i];
        break;
      }
    }
  }
  
  if (!targetTerm) {
    console.error("Could not find target solar term for Dayun calculation");
    return emptyResult;
  }
  
  // 4. Calculate Time Difference
  const diffMillis = Math.abs(targetTerm.date.diff(birthDt).toMillis());
  const diffMinutes = diffMillis / 1000 / 60;
  
  // 5. Convert to Age
  // 3 days = 1 year = 4320 mins
  // 1 day = 4 months = 1440 mins
  // 1 hour = 5 days = 60 mins
  // 12 mins = 1 day
  
  const years = Math.floor(diffMinutes / 4320);
  const rem1 = diffMinutes % 4320;
  const months = Math.floor(rem1 / 360); // 1 month = 360 mins
  const rem2 = rem1 % 360;
  const days = Math.floor(rem2 / 12); // 1 day = 12 mins
  
  // Calculate actual start date
  const startDate = birthDt.plus({ years, months, days });
  
  // Round for the list display (traditional integer age)
  const startAgeRounded = Math.round(diffMinutes / 4320);
  
  // 6. Generate Pillars
  const monthPillarStr = baziPillars.monthPillar;
  const monthGanZhi = parsePillarString(monthPillarStr);
  const monthIndex = getGanZhiIndex(monthGanZhi);
  
  const dayunList = [];
  for (let i = 1; i <= 10; i++) { // Generate 10 Dayun
    const index = isForward ? (monthIndex + i) % 60 : (monthIndex - i + 60) % 60;
    const pillar = getGanZhiWithTranslation(index, language);
    
    const currentStartAge = startAgeRounded + (i - 1) * 10;
    const currentEndAge = currentStartAge + 9;
    const startYear = birthDt.year + currentStartAge;
    const endYear = startYear + 9;
    
    dayunList.push({
      index: i,
      pillar: pillar,
      ganZhi: getGanZhiWithTranslation(index, 'zh'),
      startAge: currentStartAge,
      endAge: currentEndAge,
      startYear: startYear,
      endYear: endYear
    });
  }
  
  return {
    list: dayunList,
    startInfo: {
      years, months, days,
      startDate: startDate.toISO()
    }
  };
}

export function computeLiunian(year, language = "en") {
  let index = (year - 1864) % 60;
  if (index < 0) index += 60;
  return getGanZhiWithTranslation(index, language);
}